import { z } from "zod";

import { FormSectionSchema, type FormSection } from "./FormSection.js";
import { IncidentFormIdSchema } from "./incidentWorkItem.js";

/**
 * Admin-only manual correction of "agreed reporting fields" on an incident.
 *
 * The editable fields and where they live:
 * - `dateTimeOfDetention` — section `detentionDetails` (form type `136`), one per incident.
 * - `arrivalDateTime` — section `recordPlaceOfSafetyArrival` (form type `recordPlaceOfSafety`),
 *   one per place-of-safety form (so multiple per incident).
 * - `dateTimeLeft` — section `finishForm` (form type `finishForm`), one global per incident.
 * - `leavePos` — section `preEndIncident` (form type `preEndIncident`), the time police left
 *   recorded when the incident was ended early.
 *
 * `newValue` is a raw ISO datetime string; the server wraps it as `{ value }` before persisting,
 * so the client cannot inject arbitrary keys into the section data.
 */
export type AdminReportingFieldKey =
  "dateTimeOfDetention" | "arrivalDateTime" | "dateTimeLeft" | "leavePos";

export const AdminReportingFieldKeySchema = z.enum([
  "dateTimeOfDetention",
  "arrivalDateTime",
  "dateTimeLeft",
  "leavePos",
]) satisfies z.ZodType<AdminReportingFieldKey>;

export const AdminEditReportingFieldEditSchema = z.object({
  formId: IncidentFormIdSchema,
  sectionId: FormSectionSchema,
  fieldKey: AdminReportingFieldKeySchema,
  newValue: z.iso.datetime(),
});
export type AdminEditReportingFieldEdit = z.infer<
  typeof AdminEditReportingFieldEditSchema
>;

export const AdminEditReportingFieldsRequestSchema = z
  .object({
    reason: z.string().min(1),
    ccmNumber: z.string().min(1),
    edits: z.array(AdminEditReportingFieldEditSchema).min(1),
  })
  .refine(
    ({ edits }) => {
      const seen = new Set(
        edits.map((e) => `${e.formId}:${e.sectionId}:${e.fieldKey}`),
      );
      return seen.size === edits.length;
    },
    { message: "Only one edit is allowed per field", path: ["edits"] },
  );

export type AdminEditReportingFieldsRequest = z.infer<
  typeof AdminEditReportingFieldsRequestSchema
>;

/**
 * Plain (unbranded) shape for use as a TSOA `@Body()` type on the controller and the
 * frontend request payload. Kept structurally in sync with the zod schema above.
 */
export type AdminEditReportingFieldsRequestUnbranded = {
  reason: string;
  ccmNumber: string;
  edits: {
    formId: string;
    sectionId: FormSection;
    fieldKey: AdminReportingFieldKey;
    newValue: string;
  }[];
};
